/**
 * Mapeamento de rotas e tabs para permissões necessárias
 *
 * Este arquivo centraliza a definição de quais permissões são necessárias
 * para acessar cada rota/tab do sistema.
 */

import { FeatureTabs, settingsTabsParams } from 'utils/tabs'

// Mapeamento de feature (tab) para permissão
export const FEATURE_PERMISSIONS: Record<string, string> = {
  // Comercial
  REGISTRATION_LINK: 'register_links:show',
  PRODUCTS: 'products:show',
  CONTRACTS: 'contracts:show',
  PAYMENT_LINK: 'product_links:show',
  SALES_PAGE: 'sales_page:show',
  WORKFLOW: 'workflows:show',

  // Relacionamento
  CUSTOMERS: 'customers:show',
  COMMUNICATION: 'announcements:show',
  NOTIFICATIONS: 'notifications:view',
  PORTAL: 'portal:view',

  // Faturamento
  CHARGES: 'billings:show',
  INSTALLMENTS: 'installments:show',
  RECURRENCES: 'recurrences:show',
  NFS: 'nfses:show',
  NEGATIVATION: 'negativation:show',
  TRANSFERS: 'transfers:show',

  // Financeiro (rota /operacao)
  ANALYSIS: 'financial_analysis:view',
  ACCOUNTS_RECEIVABLE: 'accounts_receivable:show',
  ACCOUNTS_PAYABLE: 'accounts_payable:show',
  CASH_FLOW: 'cash_flow:access',
  BANK_RECONCILIATION: 'bank_reconciliation:access',
  DRE: 'dre:access',

  // Compartilhado
  IMPORTS: 'imports:show'
}

// Tabs secundárias que não devem ser usadas para redirecionamento
// (são compartilhadas entre seções e não representam a "home" de uma seção)
const SECONDARY_TABS = new Set([FeatureTabs.IMPORTS])

// Mapeamento de seções para permissões dos sub-items
export const SECTION_PERMISSIONS: Record<string, string[]> = {
  ASSISTANT: ['dashboard:view'],
  COMERCIAL: [
    'register_links:show',
    'products:show',
    'contracts:show',
    'product_links:show',
    'sales_page:show',
    'workflows:show'
  ],
  CLIENTS: [
    'customers:show',
    'announcements:show',
    'notifications:view',
    'portal:view'
  ],
  CHARGES: [
    'billings:show',
    'installments:show',
    'recurrences:show',
    'nfses:show',
    'negativation:show',
    'transfers:show'
  ],
  // Financeiro — o rótulo mudou, a chave e a rota (/operacao) continuam.
  // `reports:show` saiu: Relatórios virou modal e não tem mais entrada de menu.
  // Basta UMA destas para a seção aparecer. As três últimas entraram no SC-9544:
  // sem elas aqui, quem tivesse só Fluxo de Caixa (ou só DRE, ou só Conciliação)
  // via a aba sumir do menu, porque a seção inteira seria filtrada antes.
  OPERATION: [
    'financial_analysis:view',
    'accounts_receivable:show',
    'accounts_payable:show',
    'cash_flow:access',
    'dre:access',
    'bank_reconciliation:access'
  ]
}

// Mapeamento de rotas para permissões
// Formato: { pathname: { tab?: permission } }
interface RoutePermissionConfig {
  default?: string // Permissao padrao da rota (quando nao tem tab)
  tabs?: Record<string, string> // Permissoes por tab
}

export const ROUTE_PERMISSIONS: Record<string, RoutePermissionConfig> = {
  '/visao-geral': {
    default: 'dashboard:view'
  },
  // A ordem das abas aqui importa: `getFirstAllowedTab` devolve a primeira
  // coberta por permissão, e é para ela que o usuário cai ao abrir a rota.
  '/comercial': {
    tabs: {
      [FeatureTabs.REGISTRATION_LINK]: 'register_links:show',
      [FeatureTabs.PRODUCTS]: 'products:show',
      [FeatureTabs.CONTRACTS]: 'contracts:show',
      [FeatureTabs.PAYMENT_LINK]: 'product_links:show',
      [FeatureTabs.SALES_PAGE]: 'sales_page:show',
      [FeatureTabs.WORKFLOW]: 'workflows:show',
      [FeatureTabs.IMPORTS]: 'imports:show'
    }
  },
  // Notificações aparece no menu de Relacionamento, mas é rota própria
  // (`/notificacoes`), não aba de `/clientes` — por isso não entra aqui: se
  // entrasse, `getFirstAllowedTab` poderia mandar o usuário para
  // `/clientes?tab=notificacoes`, que não renderiza nada.
  '/clientes': {
    tabs: {
      [FeatureTabs.CUSTOMERS]: 'customers:show',
      [FeatureTabs.COMMUNICATION]: 'announcements:show',
      [FeatureTabs.PORTAL]: 'portal:view',
      [FeatureTabs.IMPORTS]: 'imports:show'
    }
  },
  '/cobrancas': {
    tabs: {
      [FeatureTabs.CHARGES]: 'billings:show',
      [FeatureTabs.INSTALLMENTS]: 'installments:show',
      [FeatureTabs.RECURRENCES]: 'recurrences:show',
      [FeatureTabs.NFS]: 'nfses:show',
      [FeatureTabs.NEGATIVATION]: 'negativation:show',
      [FeatureTabs.TRANSFERS]: 'transfers:show',
      [FeatureTabs.IMPORTS]: 'imports:show'
    }
  },
  // Financeiro. `workflow`, `notificacoes` e `relatorios` saíram daqui na
  // reorganização do menu; quem chegar por link antigo é redirecionado pelo
  // próprio `templates/Operation`, então não precisam de mapeamento de
  // permissão.
  '/operacao': {
    tabs: {
      [FeatureTabs.ANALYSIS]: 'financial_analysis:view',
      [FeatureTabs.ACCOUNTS_RECEIVABLE]: 'accounts_receivable:show',
      [FeatureTabs.ACCOUNTS_PAYABLE]: 'accounts_payable:show',
      [FeatureTabs.CASH_FLOW]: 'cash_flow:access',
      [FeatureTabs.BANK_RECONCILIATION]: 'bank_reconciliation:access',
      [FeatureTabs.DRE]: 'dre:access',
      [FeatureTabs.IMPORTS]: 'imports:show'
    }
  },
  // Configurações. Só entram aqui as abas que têm permissão correspondente no
  // registry; as demais (Perfil, Alterar senha, Meu Plano, Minhas Taxas,
  // Integrações, Banco CORA, Rastreamento) ficam de fora de propósito, porque
  // todo usuário precisa alcançá-las.
  //
  // Este mapa é a fonte única: o menu de Configurações lê as mesmas permissões
  // (settingsMenuGroups[].items[].permission) e o teste em
  // Menu.permissions.test.ts falha se os dois divergirem.
  '/settings': {
    tabs: {
      [settingsTabsParams.USERS]: 'users:show',
      [settingsTabsParams.EXTRA_FIELDS]: 'custom_fields:show',
      [settingsTabsParams.CUSTOMIZE_PORTAL]: 'portal:customize',
      [settingsTabsParams.TAGS]: 'tags:show',
      [settingsTabsParams.IMPORTS]: 'imports:show',
      [settingsTabsParams.BANK_ACCOUNTS]: 'bank_accounts:show',
      // Atenção: a aba Notificações de Configurações é a régua de cobrança
      // (chama /v1/billing_rulers/*), não o sino de notificações. A permissão
      // correta é billing_rulers:show — 'notifications:view' governa o
      // notifications#index, que é outra tela.
      [settingsTabsParams.NOTIFICATIONS]: 'billing_rulers:show',
      [settingsTabsParams.WEBHOOKS]: 'webhooks:show',
      // `integrations` tem acao unica: quem enxerga a aba e quem pode configurar a
      // integracao. Nao ha `integrations:show` para usar aqui — e proposital.
      [settingsTabsParams.INTEGRATIONS]: 'integrations:manage',
      // A aba tem ainda um filtro de ELEGIBILIDADE no menu (5 travas da RF-01, resolvidas
      // pelo GET /v3/credit_card_plans). Isto aqui é a outra metade: a permissão, que é o
      // que o backend exige em credit_card_plans#index. As duas coisas são diferentes —
      // ter permissão não torna o merchant elegível.
      [settingsTabsParams.CREDIT_CARD_PLANS]: 'credit_card_plans:show'
    }
  }
}

/**
 * Retorna a permissao necessaria para uma rota/tab especifica
 * @param pathname Caminho da rota (ex: '/cobrancas')
 * @param tab Tab atual (ex: 'cobrancas')
 * @returns Permissao necessaria ou null se nao requer permissao
 */
export function getRequiredPermission(
  pathname: string,
  tab?: string | string[]
): string | null {
  const config = ROUTE_PERMISSIONS[pathname]
  if (!config) return null

  // Se tem tab e existe configuracao de tabs
  if (tab && config.tabs) {
    const tabValue = Array.isArray(tab) ? tab[0] : tab
    return config.tabs[tabValue] || null
  }

  // Retorna permissao default da rota
  return config.default || null
}

/**
 * Retorna a primeira tab permitida para uma rota
 * @param pathname Caminho da rota
 * @param hasPermission Funcao para verificar permissao
 * @param includeSecondary Se deve incluir tabs secundarias (imports, etc)
 * @returns Tab permitida ou null
 */
export function getFirstAllowedTab(
  pathname: string,
  hasPermission: (permission: string) => boolean,
  includeSecondary = false
): string | null {
  const config = ROUTE_PERMISSIONS[pathname]
  if (!config?.tabs) return null

  for (const [tab, permission] of Object.entries(config.tabs)) {
    // Pula tabs secundarias se nao for para incluir
    if (!includeSecondary && SECONDARY_TABS.has(tab)) {
      continue
    }
    if (hasPermission(permission)) {
      return tab
    }
  }

  return null
}

/**
 * Verifica se o usuario tem acesso a pelo menos uma tab principal da rota
 * @param pathname Caminho da rota
 * @param hasAnyPermission Funcao para verificar se tem alguma permissao
 * @param includeSecondary Se deve incluir tabs secundarias (imports, etc)
 * @returns true se tem acesso a pelo menos uma tab principal
 */
export function hasAccessToRoute(
  pathname: string,
  hasAnyPermission: (permissions: string[]) => boolean,
  includeSecondary = false
): boolean {
  const config = ROUTE_PERMISSIONS[pathname]
  if (!config) return true // Rotas sem configuracao sao publicas

  if (config.default) {
    return hasAnyPermission([config.default])
  }

  if (config.tabs) {
    // Filtra tabs secundarias se nao for para incluir
    const permissions = Object.entries(config.tabs)
      .filter(([tab]) => includeSecondary || !SECONDARY_TABS.has(tab))
      .map(([, permission]) => permission)

    return hasAnyPermission(permissions)
  }

  return true
}
